import { useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { categoryStyle } from '../map/markers.js';
import { useStore } from '../store/StoreContext.js';
import { itineraryPlaces } from '../store/selectors.js';
import { moveId, reorderIds } from './reorder.js';

export interface ItineraryPanelProps {
  readonly selectedId: number | null;
  readonly onSelect: (placeId: number) => void;
}

export function ItineraryPanel({ selectedId, onSelect }: ItineraryPanelProps): ReactNode {
  const { state, reorder, demote } = useStore();
  const steps = itineraryPlaces(state);
  const ids = steps.map((place) => place.id);

  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  function commit(next: readonly number[]): void {
    if (next.length === ids.length && next.every((id, index) => id === ids[index])) return;
    // Le contrat serveur est un remplacement complet, dernier ecrivain gagnant :
    // on envoie donc toujours la liste entiere, jamais un delta.
    void reorder(next);
  }

  function onDragStart(dragEvent: DragEvent<HTMLLIElement>, id: number): void {
    setDraggedId(id);
    dragEvent.dataTransfer.effectAllowed = 'move';
    // Firefox n'amorce pas le glissement si aucune donnee n'est posee.
    dragEvent.dataTransfer.setData('text/plain', String(id));
  }

  function onDragOver(dragEvent: DragEvent<HTMLLIElement>, index: number): void {
    if (draggedId === null) return;
    dragEvent.preventDefault();
    dragEvent.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  }

  function onDrop(dragEvent: DragEvent<HTMLLIElement>, index: number): void {
    dragEvent.preventDefault();
    const id = draggedId;
    setDraggedId(null);
    setOverIndex(null);
    if (id === null) return;
    commit(reorderIds(ids, ids.indexOf(id), index));
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <h2 className="panel__title">Itineraire</h2>
        <span className="panel__count">{steps.length}</span>
      </header>

      <ol className={`panel__body steps${steps.length > 0 ? ' steps--traced' : ''}`}>
        {steps.map((place, index) => (
          <li
            key={place.id}
            className={[
              'step',
              selectedId === place.id ? 'is-current' : '',
              draggedId === place.id ? 'is-dragged' : '',
              overIndex === index && draggedId !== null && draggedId !== place.id ? 'is-over' : '',
            ]
              .filter((entry) => entry !== '')
              .join(' ')}
            style={{ '--card-color': categoryStyle(place.category).color } as CSSProperties}
            draggable
            onDragStart={(dragEvent) => onDragStart(dragEvent, place.id)}
            onDragEnd={() => {
              setDraggedId(null);
              setOverIndex(null);
            }}
            onDragOver={(dragEvent) => onDragOver(dragEvent, index)}
            onDrop={(dragEvent) => onDrop(dragEvent, index)}
            onClick={() => onSelect(place.id)}
          >
            <span className="step__index">{index + 1}</span>

            <span className="step__text">
              <span className="step__title">{place.title}</span>
              <span className="step__meta">
                <span className="step__chip" aria-hidden="true">
                  {categoryStyle(place.category).glyph}
                </span>
                {categoryStyle(place.category).label}
              </span>
            </span>

            {/*
              Boutons haut/bas en plus du glisser-deposer : l'API HTML5 de
              drag and drop n'existe pas sur les navigateurs tactiles, et ce
              voyage se prepare beaucoup depuis un telephone.
            */}
            <span className="step__moves">
              <button
                className="button button--ghost button--icon"
                type="button"
                aria-label={`Monter ${place.title}`}
                disabled={index === 0}
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation();
                  commit(moveId(ids, place.id, -1));
                }}
              >
                ▲
              </button>
              <button
                className="button button--ghost button--icon"
                type="button"
                aria-label={`Descendre ${place.title}`}
                disabled={index === steps.length - 1}
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation();
                  commit(moveId(ids, place.id, 1));
                }}
              >
                ▼
              </button>
              <button
                className="button button--ghost button--icon button--icon-danger"
                type="button"
                aria-label={`Retirer ${place.title} de l itineraire`}
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation();
                  void demote(place.id);
                }}
              >
                ✕
              </button>
            </span>
          </li>
        ))}

        {steps.length === 0 ? (
          <li className="empty">
            <span className="empty__mark" aria-hidden="true">
              ⌁
            </span>
            <p className="empty__title">Aucune etape retenue</p>
            <p className="empty__text">
              Les lieux qui font l unanimite passent ici. Promeus-en un depuis le vivier : la route
              se tracera toute seule sur la carte.
            </p>
          </li>
        ) : null}
      </ol>
    </section>
  );
}
